import {
  Button,
  grayButtonStyles,
  Icon,
  IconButton,
  NumberInputField,
  Text,
  Tooltip,
  useSelectState,
} from "@smartsuite/react-ui/lib";
import { CheckboxLabel } from "@smartsuite/react-ui/lib/components/forms/CheckboxLabel/CheckboxLabel";
import React, { memo, useRef } from "react";
import { Field } from "../../api/types";
import { RenderContext } from "../../canvas/renderer/fabric-renderer";
import {
  hasAngleAttr,
  hasDisabledHeight,
  isFieldItem,
} from "../../canvas/state/attributes";
import {
  AddressFieldType,
  DocumentDesignItem,
  DocumentDesignItemPosition,
  DocumentDesignItemType,
  DocumentDesignState,
} from "../../canvas/state/types";
import { fieldTypeIcons } from "../global.constants";
import { showElementWithoutImplementation } from "../utils";
import { getAngleInDegFromString } from "./helpers";
import { SidebarSelectionConfigFile } from "./SidebarSelectionConfigFile/SidebarSelectionConfigFile";
import { SidebarSelectionConfigImage } from "./SidebarSelectionConfigImage";
import { SidebarSelectionConfigLinkedRecord } from "./SidebarSelectionConfigLinkedRecord";
import { SidebarSelectionConfigMultipleSelect } from "./SidebarSelectionConfigMultipleSelect";
import { SidebarSelectionConfigTextField } from "./SidebarSelectionConfigTextField";
import { SidebarAction } from "../types";
import { StyledSwitchButton } from "../StyledSwitchButton/StyledSwitchButton";
import { SidebarSelectionOrderGroup } from "./SidebarSelectionOrderGroup";
import { SidebarSelectionConfigRelativeOptions } from "./SidebarSelectionConfigRelativeOptions";
import { Colors, SolutionColorVariables } from "@smartsuite/types";
import { IconSizes } from "@smartsuite/icons";

export interface SidebarSelectionConfigProps {
  selected: DocumentDesignItem;
  selectedField: Field | undefined;
  context: RenderContext;
  state: DocumentDesignState;
  onCloseSelectionConfig: () => void;
  onRemove: (item: DocumentDesignItem) => void;
  onDuplicate: (item: DocumentDesignItem) => void;
  onChangeSelected: (modifiedItem: DocumentDesignItem) => void;
  onAction: (action: SidebarAction) => void;
}

const SidebarSelectionConfigRaw: React.FC<SidebarSelectionConfigProps> = ({
  selected,
  selectedField,
  context,
  state,
  onCloseSelectionConfig,
  onRemove,
  onDuplicate,
  onChangeSelected,
  onAction,
}) => {
  const headerInfo = getElementNameWithIcon(selected.type);
  const [xSelectState, setXSelectState] = useSelectState();
  const [ySelectState, setYSelectState] = useSelectState();
  const [widthSelectState, setWidthSelectState] = useSelectState();
  const [heightSelectState, setHeightSelectState] = useSelectState();
  const [rotationSelectState, setRotationSelectState] = useSelectState();
  const xReference = useRef<HTMLInputElement>(null);
  const yReference = useRef<HTMLInputElement>(null);
  const widthReference = useRef<HTMLInputElement>(null);
  const heightReference = useRef<HTMLInputElement>(null);
  const rotationReference = useRef<HTMLInputElement>(null);

  const isAnchorPosition = selected.position === "anchored";
  const isHeightDisabled = hasDisabledHeight(selected, selectedField);

  return (
    <div className="sidebar-selection-config">
      {selectedField && (
        <div className="sidebar-selection-config__section border-bottom">
          <div className="top-bar">
            <span className="top-bar__left-column">
              <span className="top-bar__left-column__type-icon">
                <Icon
                  name={fieldTypeIcons[selectedField.field_type]}
                  size="S"
                  color="currentColor"
                />
              </span>
              {selectedField.label}
            </span>
            <IconButton
              className="top-bar__back-button"
              name="long-arrow-left"
              color={Colors.dGrey}
              hoverColor={SolutionColorVariables.sc1}
              ariaLabel="Close"
              onClick={() => onCloseSelectionConfig()}
            />
          </div>
        </div>
      )}
      {!selectedField && (
        <div className="sidebar-selection-config__section border-bottom">
          <div className="top-bar">
            <span className="top-bar__left-column">
              <span className="top-bar__left-column__type-icon">
                <Icon
                  name={headerInfo.icon}
                  size={headerInfo.iconSize}
                  color={SolutionColorVariables.sc1}
                />
              </span>
              {headerInfo.name}
            </span>
            <IconButton
              className="top-bar__back-button"
              name="long-arrow-left"
              ariaLabel="Close"
              color={Colors.dGrey}
              hoverColor={SolutionColorVariables.sc1}
              onClick={() => onCloseSelectionConfig()}
            />
          </div>
        </div>
      )}

      <div className="sidebar-selection-config__section overflow">
        <div className="sidebar-selection-config__section__split-column">
          <div>
            <div style={{ marginBottom: 4 }}>
              <Text
                weight="medium"
                color={xSelectState ? "link" : "primary"}
                wrapMode="ellipsis"
              >
                X
              </Text>
            </div>
            <NumberInputField
              ref={xReference}
              onFocus={() => setXSelectState(true)}
              onBlur={() => setXSelectState(false)}
              state={xSelectState ? "active" : undefined}
              showSymbol={false}
              autoHideNavigation={true}
              inputStyles={{
                backgroundColor: "white",
                borderColor: "G-1-5",
                borderHoverColor: "G-3",
                borderFocusColor: "G-3",
              }}
              max="99999"
              min="0"
              value={`${selected.left}`}
              onChangeValue={(value) => {
                if (xReference.current) xReference.current.focus();
                onChangeSelected({
                  ...selected,
                  left: parseIntegerValue(value),
                });
              }}
            />
          </div>
          <div className="sidebar-selection-config__field sidebar-selection-config__field--checkbox">
            <div>
              <div style={{ marginBottom: 4 }}>
                <Text
                  weight="medium"
                  color={ySelectState ? "link" : "primary"}
                  wrapMode="ellipsis"
                >
                  Y
                </Text>
              </div>
              <Tooltip
                text={`This element's Y position will follow anchor's Y because of the "Anchored" property`}
                disabled={!isAnchorPosition}
                target={
                  <NumberInputField
                    className="sidebar-selection-config__field__input"
                    ref={yReference}
                    onFocus={() => setYSelectState(true)}
                    onBlur={() => setYSelectState(false)}
                    state={ySelectState ? "active" : undefined}
                    showSymbol={false}
                    autoHideNavigation={true}
                    inputStyles={{
                      backgroundColor: "white",
                      borderColor: "G-1-5",
                      borderHoverColor: isAnchorPosition ? undefined : "G-3",
                      borderFocusColor: isAnchorPosition ? undefined : "G-3",
                      inputColor: isAnchorPosition ? "inactive" : undefined,
                    }}
                    max="99999"
                    min="0"
                    value={`${selected.top}`}
                    onChangeValue={(value) => {
                      if (yReference.current) yReference.current.focus();
                      onChangeSelected({
                        ...selected,
                        top: parseIntegerValue(value),
                      });
                    }}
                    disabled={isAnchorPosition}
                  />
                }
              />
            </div>
          </div>
        </div>
        <div className="sidebar-selection-config__section__split-column">
          <div>
            <div style={{ marginBottom: 4 }}>
              <Text
                weight="medium"
                color={widthSelectState ? "link" : "primary"}
                wrapMode="ellipsis"
              >
                Width
              </Text>
            </div>
            <NumberInputField
              ref={widthReference}
              onFocus={() => setWidthSelectState(true)}
              onBlur={() => setWidthSelectState(false)}
              state={widthSelectState ? "active" : undefined}
              showSymbol={false}
              autoHideNavigation={true}
              inputStyles={{
                backgroundColor: "white",
                borderColor: "G-1-5",
                borderHoverColor: "G-3",
                borderFocusColor: "G-3",
              }}
              max="1000"
              min="1"
              value={`${selected.width}`}
              onChangeValue={(value) => {
                if (widthReference.current) widthReference.current.focus();
                onChangeSelected({ ...selected, width: parseInt(value) });
              }}
            />
          </div>

          <div className="sidebar-selection-config__field sidebar-selection-config__field--checkbox">
            <div>
              <div style={{ marginBottom: 4 }}>
                <Text
                  weight="medium"
                  color={heightSelectState ? "link" : "primary"}
                  wrapMode="ellipsis"
                >
                  Height
                </Text>
              </div>
              <NumberInputField
                ref={heightReference}
                onFocus={() => setHeightSelectState(true)}
                onBlur={() => setHeightSelectState(false)}
                state={heightSelectState ? "active" : undefined}
                showSymbol={false}
                autoHideNavigation={true}
                className="sidebar-selection-config__field__input"
                inputStyles={{
                  backgroundColor: "white",
                  borderColor: "G-1-5",
                  borderHoverColor: isHeightDisabled ? undefined : "G-3",
                  borderFocusColor: isHeightDisabled ? undefined : "G-3",
                  inputColor: isHeightDisabled ? "inactive" : undefined,
                }}
                max="1000"
                min="1"
                value={`${selected.height}`}
                onChangeValue={(value) => {
                  if (heightReference.current) heightReference.current.focus();
                  onChangeSelected({ ...selected, height: parseInt(value) });
                }}
                disabled={isHeightDisabled}
              />
            </div>

            {showElementWithoutImplementation && (
              <CheckboxLabel
                label="Auto"
                checked={true}
                onChange={() => {}} // TODO: Set height auto for element
                className="sidebar-selection-config__checkbox"
              />
            )}
          </div>
        </div>

        {hasAngleAttr(selected) && (
          <div>
            <div style={{ marginBottom: 4 }}>
              <Text
                weight="medium"
                color={rotationSelectState ? "link" : "primary"}
                wrapMode="ellipsis"
              >
                Rotation
              </Text>
            </div>
            <NumberInputField
              ref={rotationReference}
              onFocus={() => setRotationSelectState(true)}
              onBlur={() => setRotationSelectState(false)}
              state={rotationSelectState ? "active" : undefined}
              showSymbol={false}
              autoHideNavigation={true}
              inputStyles={{
                backgroundColor: "white",
                borderColor: "G-1-5",
                borderHoverColor: "G-3",
                borderFocusColor: "G-3",
              }}
              max="360"
              min="0"
              value={`${selected.angle ?? 0}`}
              onChangeValue={(value) => {
                if (rotationReference.current)
                  rotationReference.current.focus();
                onChangeSelected({
                  ...selected,
                  angle: getAngleInDegFromString(value),
                });
              }}
            />
          </div>
        )}

        <div>
          <div style={{ marginBottom: 4 }}>
            <Text weight="medium" wrapMode="ellipsis">
              Position
            </Text>
          </div>
          <StyledSwitchButton
            options={[
              { label: "Absolute", value: "absolute" },
              { label: "Relative", value: "relative" },
              { label: "Anchored", value: "anchored" },
            ]}
            currentValue={selected.position ?? "absolute"}
            onChange={(value) =>
              onChangeSelected({
                ...selected,
                position: value as DocumentDesignItemPosition,
                anchorId: undefined,
                anchorDistance: 0,
              })
            }
          />
        </div>

        {isAnchorPosition && (
          <SidebarSelectionConfigRelativeOptions
            state={state}
            context={context}
            selected={selected}
            onChangeSelected={onChangeSelected}
          />
        )}

        <div>
          <div style={{ marginBottom: 4 }}>
            <Text weight="medium" wrapMode="ellipsis">
              Order
            </Text>
          </div>
          <SidebarSelectionOrderGroup onAction={onAction} />
        </div>

        {(selected.type === DocumentDesignItemType.linkedRecord ||
          selected.type === DocumentDesignItemType.subitems2) && (
          <SidebarSelectionConfigLinkedRecord
            selected={selected}
            context={context}
            onChangeSelected={onChangeSelected}
          />
        )}
        {selected.type === DocumentDesignItemType.multipleSelect && (
          <SidebarSelectionConfigMultipleSelect
            selected={selected}
            onChangeSelected={onChangeSelected}
          />
        )}
        {selected.type === DocumentDesignItemType.file && (
          <SidebarSelectionConfigFile
            selected={selected}
            onChangeSelected={onChangeSelected}
          />
        )}
        {selected.type === DocumentDesignItemType.image && (
          <SidebarSelectionConfigImage
            context={context}
            selected={selected}
            onChangeSelected={onChangeSelected}
          />
        )}
        {selected.type !== DocumentDesignItemType.linkedRecord &&
          selected.type !== DocumentDesignItemType.subitems2 &&
          selected.type !== DocumentDesignItemType.file &&
          selected.type !== DocumentDesignItemType.image &&
          selected.type !== DocumentDesignItemType.signature && (
            <SidebarSelectionConfigTextField
              selected={selected}
              onChangeSelected={onChangeSelected}
            />
          )}

        {isFieldItem(selected) &&
          selectedField?.field_type === "addressfield" && (
            <div>
              <div style={{ marginBottom: 4 }}>
                <Text
                  weight="medium"
                  color={rotationSelectState ? "link" : "primary"}
                  wrapMode="ellipsis"
                >
                  Address Type
                </Text>
              </div>
              <StyledSwitchButton
                options={[
                  { label: "Plain Text", value: "plain-text" },
                  { label: "Link", value: "link" },
                  { label: "Map", value: "map" },
                ]}
                currentValue={selected.addressType ?? "plain-text"}
                onChange={(value) => {
                  onChangeSelected({
                    ...selected,
                    addressType: value as AddressFieldType,
                  });
                }}
              />
            </div>
          )}

        <div className="sidebar-selection-config__section__split-column">
          <Button
            styles={grayButtonStyles}
            onClick={() => onDuplicate(selected)}
            iconSize="S"
            iconName="layers"
          >
            Duplicate
          </Button>

          <Button
            styles={grayButtonStyles}
            onClick={() => onRemove(selected)}
            iconSize="S"
            iconName="trash-1"
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
};

export const SidebarSelectionConfig = memo(SidebarSelectionConfigRaw);

const getElementNameWithIcon = (
  type: Partial<DocumentDesignItemType>,
): { name: string; icon: string; iconSize: IconSizes } => {
  const elementNameRegistry: Record<
    DocumentDesignItemType,
    { name: string; icon: string; iconSize: IconSizes }
  > = {
    [DocumentDesignItemType.text]: {
      name: "Text",
      icon: "text",
      iconSize: "S",
    },
    [DocumentDesignItemType.link]: {
      name: "Link",
      icon: "link",
      iconSize: "S",
    },
    [DocumentDesignItemType.line]: {
      name: "Line",
      icon: "horizontal-line",
      iconSize: "M",
    },
    [DocumentDesignItemType.rect]: {
      name: "Rect",
      icon: "card-big",
      iconSize: "M",
    },
    [DocumentDesignItemType.circle]: {
      name: "Circle",
      icon: "circle",
      iconSize: "M",
    },
    [DocumentDesignItemType.field]: {
      name: "Field",
      icon: "circle",
      iconSize: "S",
    },
    [DocumentDesignItemType.textarea]: {
      name: "Textarea",
      icon: "circle",
      iconSize: "S",
    },
    [DocumentDesignItemType.linkedRecord]: {
      name: "Linked Record",
      icon: "circle",
      iconSize: "S",
    },
    [DocumentDesignItemType.subitems2]: {
      name: "Sub-Items",
      icon: "circle",
      iconSize: "S",
    },
    [DocumentDesignItemType.multipleSelect]: {
      name: "Multiple Select",
      icon: "multiple-select",
      iconSize: "S",
    },
    [DocumentDesignItemType.file]: {
      name: "File",
      icon: "circle",
      iconSize: "S",
    },
    [DocumentDesignItemType.image]: {
      name: "Image",
      icon: "image",
      iconSize: "S",
    },
    [DocumentDesignItemType.signature]: {
      name: "Signature",
      icon: "image",
      iconSize: "S",
    },
  };

  return elementNameRegistry[type];
};

function parseIntegerValue(value: string | undefined): number {
  const parsedValue = parseInt(value ?? "0");
  if (isNaN(parsedValue)) {
    return 0;
  }

  return parsedValue;
}
